import React from 'react';
import { FunnelElement } from '../../types/element.types';
import { useFunnelStore } from '../../store/funnelStore';
import ImageUpload from './ImageUpload';

interface ContentTabProps {
  element: FunnelElement;
}

/**
 * Content Tab Component
 * Allows editing element content (text, images, links, etc.)
 */
const ContentTab: React.FC<ContentTabProps> = ({ element }) => {
  const { updateElement } = useFunnelStore();

  const handleContentChange = (value: any) => {
    updateElement(element.id, { content: value });
  };

  const handleSettingChange = (key: string, value: any) => {
    updateElement(element.id, {
      settings: { ...element.settings, [key]: value },
    });
  };

  // Helper to render input with live update indicator
  const renderInput = (
    label: string,
    value: string,
    onChange: (value: string) => void,
    placeholder?: string,
    type: string = 'text'
  ) => (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
        <span className="ml-2 text-xs text-primary-600">● Live</span>
      </label>
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border-2 border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
      />
    </div>
  );

  // Render content editor based on element type
  const renderContentEditor = () => {
    switch (element.type) {
      case 'heading':
        return (
          <>
            <div className="mb-2 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-xs text-green-700 font-medium">
                ✓ Changes appear instantly on canvas
              </p>
            </div>
            
            {renderInput(
              'Heading Text',
              element.content || '',
              handleContentChange,
              'Enter your heading...'
            )}
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Heading Level
                <span className="ml-2 text-xs text-primary-600">● Live</span>
              </label>
              <select
                value={element.settings?.level || 'h2'}
                onChange={(e) => handleSettingChange('level', e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
              >
                <option value="h1">H1 (Largest)</option>
                <option value="h2">H2 (Very Large)</option>
                <option value="h3">H3 (Large)</option>
                <option value="h4">H4 (Medium)</option>
                <option value="h5">H5 (Small)</option>
                <option value="h6">H6 (Smallest)</option>
              </select>
            </div>
          </>
        );

      case 'text':
        return (
          <>
            <div className="mb-2 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-xs text-green-700 font-medium">
                ✓ Changes appear instantly on canvas
              </p>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Text Content
                <span className="ml-2 text-xs text-primary-600">● Live</span>
              </label>
              <textarea
                value={element.content || ''}
                onChange={(e) => handleContentChange(e.target.value)}
                rows={6}
                placeholder="Enter your text here..."
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
              />
              <p className="text-xs text-gray-500 mt-1">
                💡 Tip: Use this for paragraphs and body text
              </p>
            </div>
          </>
        );

      case 'button':
        return (
          <>
            <div className="mb-2 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-xs text-green-700 font-medium">
                ✓ Changes appear instantly on canvas
              </p>
            </div>
            
            {renderInput(
              'Button Text',
              element.content || '',
              handleContentChange,
              'Click Me'
            )}
            
            {renderInput(
              'Link URL',
              element.settings?.link || '',
              (value) => handleSettingChange('link', value),
              'https://example.com',
              'url'
            )}
            
            <div className="mb-4">
              <label className="flex items-center gap-2 p-3 border-2 border-gray-200 rounded-[7px] hover:border-primary-300 cursor-pointer transition-all">
                <input
                  type="checkbox"
                  checked={element.settings?.openInNewTab || false}
                  onChange={(e) => handleSettingChange('openInNewTab', e.target.checked)}
                  className="w-4 h-4 text-primary-600"
                />
                <span className="text-sm font-medium text-gray-700">Open link in new tab</span>
              </label>
            </div>
          </>
        );

      case 'image':
        return (
          <>
            {/* Image Upload/URL Section */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Image Source
              </label>
              
              {/* Tab switcher for Upload vs URL */}
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => {
                    // Switch to upload mode - clear URL if it's a regular URL
                    if (element.content?.src && !element.content.src.startsWith('data:')) {
                      handleContentChange({ ...element.content, src: '' });
                    }
                  }}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all ${
                    !element.content?.src || element.content.src.startsWith('data:')
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  📤 Upload
                </button>
                <button
                  type="button"
                  onClick={() => {
                    // Switch to URL mode - clear data URL
                    if (element.content?.src && element.content.src.startsWith('data:')) {
                      handleContentChange({ ...element.content, src: '' });
                    }
                  }}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all ${
                    element.content?.src && !element.content.src.startsWith('data:')
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  🔗 URL
                </button>
              </div>

              {/* Upload Mode */}
              {(!element.content?.src || element.content.src.startsWith('data:')) && (
                <ImageUpload
                  currentImage={element.content?.src || ''}
                  onImageChange={(dataUrl) =>
                    handleContentChange({ ...element.content, src: dataUrl })
                  }
                />
              )}

              {/* URL Mode */}
              {element.content?.src && !element.content.src.startsWith('data:') && (
                <div>
                  <input
                    type="url"
                    value={element.content?.src || ''}
                    onChange={(e) =>
                      handleContentChange({ ...element.content, src: e.target.value })
                    }
                    placeholder="https://example.com/image.jpg"
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {element.content?.src && (
                    <div className="mt-3">
                      <img
                        src={element.content.src}
                        alt="Preview"
                        className="w-full h-auto max-h-48 object-contain rounded-lg border border-gray-200"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x200?text=Invalid+URL';
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Alt Text */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Alt Text
                <span className="ml-2 text-xs text-gray-500">(for accessibility)</span>
              </label>
              <input
                type="text"
                value={element.content?.alt || ''}
                onChange={(e) =>
                  handleContentChange({ ...element.content, alt: e.target.value })
                }
                placeholder="Image description"
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Link (Optional) */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Link (Optional)
              </label>
              <input
                type="url"
                value={element.settings?.link || ''}
                onChange={(e) => handleSettingChange('link', e.target.value)}
                placeholder="https://example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </>
        );

      case 'video':
        return (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Video URL
              </label>
              <input
                type="url"
                value={element.content?.url || ''}
                onChange={(e) =>
                  handleContentChange({ ...element.content, url: e.target.value })
                }
                placeholder="https://youtube.com/watch?v=..."
                className="w-full px-3 py-2 border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Provider
              </label>
              <select
                value={element.content?.provider || 'youtube'}
                onChange={(e) =>
                  handleContentChange({ ...element.content, provider: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="youtube">YouTube</option>
                <option value="vimeo">Vimeo</option>
                <option value="custom">Custom</option>
              </select>
            </div>
          </>
        );

      case 'input':
      case 'textarea':
        return (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Label
              </label>
              <input
                type="text"
                value={element.settings?.label || ''}
                onChange={(e) => handleSettingChange('label', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Placeholder
              </label>
              <input
                type="text"
                value={element.settings?.placeholder || ''}
                onChange={(e) => handleSettingChange('placeholder', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="mb-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={element.settings?.required || false}
                  onChange={(e) => handleSettingChange('required', e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="text-sm text-gray-700">Required field</span>
              </label>
            </div>
          </>
        );

      case 'checkbox':
      case 'select':
        return (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Label
              </label>
              <input
                type="text"
                value={element.settings?.label || ''}
                onChange={(e) => handleSettingChange('label', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="mb-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={element.settings?.required || false}
                  onChange={(e) => handleSettingChange('required', e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="text-sm text-gray-700">Required field</span>
              </label>
            </div>
          </>
        );

      case 'customHtml':
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              HTML Code
            </label>
            <textarea
              value={element.content || ''}
              onChange={(e) => handleContentChange(e.target.value)}
              rows={10}
              className="w-full px-3 py-2 border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono text-sm"
              placeholder="<div>Your HTML here</div>"
            />
          </div>
        );

      default:
        return (
          <div className="text-sm text-gray-500">
            Content editing not available for this element type
          </div>
        );
    }
  };

  return <div className="space-y-4">{renderContentEditor()}</div>;
};

export default ContentTab;

